import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizationContext as useOrganization } from '../contexts/OrganizationContext';
import { getRuns, createRun, getRun, deleteRun } from '../services/apiService';
import type { RunRecord, RunDetailResponse } from '../services/apiService';
import { useDemoMode } from '../contexts/DemoModeContext';
import { MOCK_RUNS, MOCK_PROSPECTS } from '../data/mockData';

const ACTIVE_RUN_STATUSES = new Set(['queued', 'processing', 'running', 'pending', 'pending_approval', 'launching']);

/**
 * Returns a polling interval (ms) for active runs using exponential backoff.
 * Polls fast at first, then slows down to avoid hammering the API.
 * Returns false when the run has reached a terminal state.
 */
function getPollingInterval(data: { status?: string } | undefined, pollCount: number): number | false {
  if (!data || !ACTIVE_RUN_STATUSES.has(data.status ?? '')) return false;
  if (pollCount < 12) return 5_000;   // first minute: every 5s
  if (pollCount < 24) return 15_000;  // next 3 min: every 15s
  if (pollCount < 60) return 30_000;  // next 18 min: every 30s
  return false; // stop after ~22 minutes total
}

/**
 * Options for configuring the useRuns hook
 */
interface UseRunsOptions {
  /** Page number for pagination (default: 1) */
  page?: number;
  /** Number of items per page (default: 20) */
  pageSize?: number;
  /** Filter runs by status (e.g., 'active', 'completed', 'failed') */
  status?: string;
}

/**
 * Hook to fetch all runs for the current organization with pagination support
 * 
 * @param options - Optional configuration for pagination and filtering
 * @param options.page - Page number for pagination (default: 1)
 * @param options.pageSize - Number of items per page (default: 20)
 * @param options.status - Filter runs by status
 * 
 * @returns Object containing:
 *   - runs: Array of run objects for the current page
 *   - isLoading: Boolean indicating if the query is loading
 *   - error: Error object if the query failed
 *   - refetch: Function to manually refetch the data
 *   - totalCount: Total number of runs across all pages
 * 
 * @example
 * ```tsx
 * // Basic usage
 * const { runs, isLoading, totalCount } = useRuns();
 * 
 * // With pagination
 * const { runs, isLoading, totalCount } = useRuns({ page: 2, pageSize: 10 });
 * 
 * // With status filter
 * const { runs, isLoading, totalCount } = useRuns({ 
 *   page: 1, 
 *   pageSize: 20, 
 *   status: 'active' 
 * });
 * 
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * 
 * return (
 *   <div>
 *     {runs.map(run => <RunCard key={run.id} run={run} />)}
 *     <Pagination 
 *       currentPage={page} 
 *       totalCount={totalCount} 
 *       pageSize={pageSize}
 *     />
 *   </div>
 * );
 * ```
 */
export function useRuns(options: UseRunsOptions = {}) {
  const { page = 1, pageSize = 20, status } = options;
  const { getToken } = useAuth();
  const { currentOrg } = useOrganization();
  const { isDemoMode } = useDemoMode();
  const orgId = currentOrg?.id;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['runs', orgId, { page, pageSize, status }, isDemoMode],
    queryFn: async () => {
      if (isDemoMode) {
        const runs = status ? MOCK_RUNS.filter(r => r.status === status) : MOCK_RUNS;
        const start = (page - 1) * pageSize;
        return { runs: runs.slice(start, start + pageSize) as RunRecord[], totalCount: runs.length };
      }
      const response = await getRuns(getToken, page, pageSize);
      if (status && response.runs) {
        const filteredRuns = response.runs.filter((run: RunRecord) => run.status === status);
        return { runs: filteredRuns, totalCount: filteredRuns.length };
      }
      return response;
    },
    enabled: !!orgId || isDemoMode,
    staleTime: 0, // Always refetch on mount so company/status never shows stale data
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    // Poll every 15s when any run on the current page is still active
    refetchInterval: (query) => {
      const runs: Array<{ status?: string }> = query.state.data?.runs ?? [];
      const hasActive = runs.some((r) => ACTIVE_RUN_STATUSES.has(r.status ?? ''));
      return hasActive && !isDemoMode ? 15_000 : false;
    },
    refetchIntervalInBackground: false,
  });

  return {
    runs: data?.runs || [],
    isLoading,
    error,
    refetch,
    totalCount: data?.totalCount || 0,
  };
}

/**
 * Hook to create a new run with optimistic updates
 * 
 * Features:
 * - Optimistic UI updates (immediate feedback)
 * - Automatic rollback on error
 * - Cache invalidation on success
 * - Prevents race conditions with cancelQueries
 * 
 * @returns Object containing:
 *   - createRun: Mutation function to create a new run
 *   - isCreating: Boolean indicating if the mutation is in progress
 *   - error: Error object if the mutation failed
 * 
 * @example
 * ```tsx
 * const { createRun, isCreating, error } = useCreateRun();
 * 
 * const handleSubmit = async (formData) => {
 *   try {
 *     const newRun = await createRun({
 *       jobUrl: formData.jobUrl,
 *       jobTitle: formData.jobTitle,
 *       company: formData.company,
 *       notes: formData.notes,
 *     });
 *     console.log('Created run:', newRun);
 *     // UI already shows the new run optimistically!
 *   } catch (err) {
 *     console.error('Failed to create run:', err);
 *     // UI automatically rolled back to previous state
 *   }
 * };
 * ```
 */
export function useCreateRun() {
  const { getToken } = useAuth();
  const { currentOrg } = useOrganization();
  const queryClient = useQueryClient();
  const orgId = currentOrg?.id;

  const mutation = useMutation({
    mutationFn: async (data: {
      jobUrl: string;
      jobTitle: string;
      company: string;
      notes?: string;
    }) => {
      // API call to create run
      return await createRun(data, getToken);
    },
    
    // Optimistic update before the mutation starts
    onMutate: async (newRunData) => {
      // Cancel any outgoing refetches to prevent race conditions
      // This ensures our optimistic update won't be overwritten
      await queryClient.cancelQueries({ queryKey: ['runs', orgId] });
      
      // Snapshot the previous value for rollback
      // We need to get all queries that match the runs pattern since we have pagination
      const previousRunsQueries = queryClient.getQueriesData({ 
        queryKey: ['runs', orgId] 
      });
      
      // Create optimistic run object with temporary ID
      const optimisticRun = {
        id: `temp-${Date.now()}`,
        jobUrl: newRunData.jobUrl,
        jobTitle: newRunData.jobTitle,
        company: newRunData.company,
        notes: newRunData.notes,
        status: 'queued',
        createdAt: new Date().toISOString(),
        prospectsFound: 0,
        campaignStatus: 'draft',
      };
      
      // Optimistically update all runs queries (all pages/filters)
      // This ensures the new run appears immediately in the UI
      queryClient.setQueriesData(
        { queryKey: ['runs', orgId] },
        (old: Record<string, unknown> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            runs: [optimisticRun, ...((old as { runs?: RunRecord[] }).runs || [])],
            totalCount: ((old as { totalCount?: number }).totalCount || 0) + 1,
          };
        }
      );
      
      // Return context with previous state for rollback
      return { previousRunsQueries };
    },
    
    // Rollback on error
    onError: (err, newRunData, context) => {
      // Restore all queries to their previous state
      if (context?.previousRunsQueries) {
        context.previousRunsQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    
    // Always refetch after mutation settles (success or error)
    onSettled: () => {
      // Invalidate all runs queries to ensure data is fresh
      // This will trigger a background refetch for any active queries
      queryClient.invalidateQueries({ queryKey: ['runs', orgId] });
    },
  });

  return {
    createRun: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Hook to fetch a single run by ID
 * 
 * @param runId - The unique identifier of the run to fetch
 * 
 * @returns Object containing:
 *   - run: Run object with full details including prospects and messages
 *   - isLoading: Boolean indicating if the query is loading
 *   - error: Error object if the query failed
 * 
 * @example
 * ```tsx
 * const { run, isLoading, error } = useRun('run_123');
 * 
 * if (isLoading) return <div>Loading run details...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * if (!run) return <div>Run not found</div>;
 * 
 * return (
 *   <div>
 *     <h1>{run.jobTitle} at {run.company}</h1>
 *     <p>Status: {run.status}</p>
 *     <ProspectsList prospects={run.prospects} />
 *   </div>
 * );
 * ```
 */
export function useRun(runId: string | undefined) {
  const { getToken, isAuthenticated } = useAuth();
  const { isDemoMode } = useDemoMode();
  const pollCountRef = useRef(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ['run', runId, isDemoMode],
    queryFn: async () => {
      if (!runId) throw new Error('Run ID is required');

      if (isDemoMode) {
        const mockRun = MOCK_RUNS.find(r => r.id === runId);
        if (!mockRun) throw new Error('Demo run not found');
        const mockProspects = (MOCK_PROSPECTS as Record<string, unknown>[]).filter(
          p => p['run_id'] === runId
        );
        return { run: mockRun as RunRecord, prospects: mockProspects as RunDetailResponse['prospects'] };
      }

      const result = await getRun(runId, getToken);
      pollCountRef.current += 1;
      return result;
    },
    // Don't fire until the user is authenticated — prevents "Auth required" errors on refresh
    enabled: !!runId && (isAuthenticated || isDemoMode),
    refetchInterval: (query) => {
      if (isDemoMode) return false;
      const runData = query.state.data as RunDetailResponse | undefined;
      return getPollingInterval(runData?.run, pollCountRef.current);
    },
    refetchIntervalInBackground: false,
  });

  return {
    run: data,
    isLoading,
    error,
  };
}

/**
 * Hook to delete a run with optimistic cache removal
 */
export function useDeleteRun() {
  const { getToken } = useAuth();
  const { currentOrg } = useOrganization();
  const queryClient = useQueryClient();
  const orgId = currentOrg?.id;

  const mutation = useMutation({
    mutationFn: (runId: string) => deleteRun(runId, getToken),

    onMutate: async (runId) => {
      await queryClient.cancelQueries({ queryKey: ['runs', orgId] });
      const previousRunsQueries = queryClient.getQueriesData({ queryKey: ['runs', orgId] });

      // Optimistically remove the run from all cached query results
      queryClient.setQueriesData(
        { queryKey: ['runs', orgId] },
        (old: Record<string, unknown> | undefined) => {
          if (!old) return old;
          const runs = ((old as { runs?: RunRecord[] }).runs || []).filter((r) => r.id !== runId);
          return {
            ...old,
            runs,
            totalCount: Math.max(0, ((old as { totalCount?: number }).totalCount || 0) - 1),
          };
        }
      );

      return { previousRunsQueries };
    },

    onError: (_err, _runId, context) => {
      if (context?.previousRunsQueries) {
        context.previousRunsQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['runs', orgId] });
    },
  });

  return {
    deleteRun: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error,
  };
}